/**
 * Workspace Assets Service
 * Client-side API layer for workspace file upload and deletion.
 * Endpoint reference: KPMG.ClaraAI.Chat.WebAPI/Controllers/FileStorageController.cs
 */

import { environment } from '../environments/environment';
import { getAccessToken } from '../auth/msalConfig';
import type { AssetItem, LooseObject } from '../features/workspace/assetmodel';

interface ContextEntry {
  key: string;
  value: string;
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function resolveFileStorageBaseUrl(): string {
  const explicitBase =
    (import.meta.env.VITE_pubSubApiBaseUrl as string | undefined)?.trim();

  if (explicitBase) {
    const normalized = trimTrailingSlash(explicitBase);
    if (/\/FileStorage$/i.test(normalized)) {
      return normalized;
    }
    return /\/api\/v\d+$/i.test(normalized)
      ? `${normalized}/FileStorage`
      : `${normalized}/api/v2/FileStorage`;
  }

  return `${trimTrailingSlash(environment.apiBaseUrl)}/FileStorage`;
}

const BASE_URL = resolveFileStorageBaseUrl();

function resolveDocumentBaseUrl(): string {
  const explicitBase =
    (import.meta.env.VITE_pubSubApiBaseUrl as string | undefined)?.trim();

  if (explicitBase) {
    const normalized = trimTrailingSlash(explicitBase);
    if (/\/Document$/i.test(normalized)) {
      return normalized;
    }
    return /\/api\/v\d+$/i.test(normalized)
      ? `${normalized}/Document`
      : `${normalized}/api/v2/Document`;
  }

  return `${trimTrailingSlash(environment.apiBaseUrl)}/Document`;
}

export const WORKSPACE_DOCUMENT_BASE_URL = resolveDocumentBaseUrl();

async function authHeaders(aiIntegrationIdOverride?: string): Promise<HeadersInit> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const aiIntegrationId = aiIntegrationIdOverride?.trim() || environment.aiIntegrationId;
  if (aiIntegrationId) headers.aiintegrationid = aiIntegrationId;
  return headers;
}

function readAssetString(asset: AssetItem, keys: readonly string[]): string {
  const record = asset as unknown as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return '';
}

function resolveAssetDataSourceId(asset: AssetItem): string {
  return readAssetString(asset, [
    'DataSourceId',
    'DatasourceId',
    'dataSourceId',
    'AgentId',
    'agentId',
  ]) || ((import.meta.env.VITE_AGENT_ID as string | undefined)?.trim() ?? '');
}

function resolveAssetBlobPath(asset: AssetItem): string {
  const rawPath = readAssetString(asset, [
    'blobFilePath',
    'BlobFilePath',
    'FilePath',
    'filePath',
  ]);

  return rawPath.replace(/\\/g, '/').replace(/^\/+/, '');
}

// function resolveAssetFileName(asset: AssetItem): string {
//   return readAssetString(asset, ['FileName', 'OriginalFileName', 'Name', 'fileName', 'name']) || 'document.docx';
// }

function toContextValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function isContextEntry(value: unknown): value is ContextEntry {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return typeof record.key === 'string' && 'value' in record;
}

function resolveAssetContext(asset: AssetItem): ContextEntry[] {
  const properties = (asset as unknown as Record<string, unknown>).Properties;
  if (Array.isArray(properties)) {
    return properties
      .filter(isContextEntry)
      .map((entry) => ({ key: entry.key, value: toContextValue(entry.value) }));
  }

  if (!properties || typeof properties !== 'object') {
    return [];
  }

  return Object.entries(properties as LooseObject).map(([key, value]) => ({
    key,
    value: toContextValue(value),
  }));
}

export function getWorkspaceAssetId(asset: AssetItem): string {
  return readAssetString(asset, ['id', 'FileId', 'fileId']) || asset.FileName || asset.Name || 'asset';
}

/**
 * POST /FileStorage/DataSource/{dataSourceId}/ContentFile/{fileName}
 * Mirrors the Angular GetFileContent flow and returns the PDF/content blob.
 */
export async function getWorkspaceAssetContent(asset: AssetItem, signal?: AbortSignal): Promise<Blob> {
  const dataSourceId = resolveAssetDataSourceId(asset);
  const blobPath = resolveAssetBlobPath(asset);

  if (!dataSourceId) {
    throw new Error('Unable to resolve asset datasource id.');
  }

  if (!blobPath) {
    throw new Error('Unable to resolve asset blob path.');
  }

  const contentFile = blobPath.replaceAll('/', '~');
  const encodedFilename = encodeURIComponent(contentFile);
  const aiIntegrationId = readAssetString(asset, ['AIIntegrationId', 'aiintegrationId', 'aiIntegrationId']);
  const headers = await authHeaders(aiIntegrationId);

  const response = await fetch(
    `${BASE_URL}/DataSource/${encodeURIComponent(dataSourceId)}/ContentFile/${encodedFilename}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify(resolveAssetContext(asset)),
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(`File content failed: ${response.status} ${response.statusText}`);
  }

  return response.blob();
}

/**
 * POST /Document/GetAgentDocument
 * Mirrors Clara AI's Syncfusion document viewer import path for DOCX assets.
 */


export interface WorkspaceFileUploadRequest {
  documentLockerId: string;
  sessionId: string;
  files: File[];
  /** Optional query params forwarded to the upload endpoint */
  contextType?: string;
  workspaceObjectiveId?: string;
  pbcRequestId?: string;
  requestName?: string;
  assetType?: string;
  docType?: string;
  sourceType?: string;
}

/**
 * POST /filestorage/documentLockerID/{documentLockerId}/sessionId/{sessionId}
 * Returns 202 Accepted; the background processor streams final status via SignalR.
 */
export async function uploadWorkspaceFiles(
  request: WorkspaceFileUploadRequest,
): Promise<void> {
  const { documentLockerId, sessionId, files, ...meta } = request;

  const params = new URLSearchParams();
  Object.entries(meta).forEach(([key, value]) => {
    if (value != null && value !== '') params.append(key, String(value));
  });

  const qs = params.toString();
  const url = `${BASE_URL}/documentLockerID/${encodeURIComponent(documentLockerId)}/sessionId/${encodeURIComponent(sessionId)}${qs ? `?${qs}` : ''}`;

  const formData = new FormData();
  files.forEach((file) => formData.append('files', file, file.name));

  const headers = await authHeaders();
  const response = await fetch(url, { method: 'POST', headers, body: formData });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }
}

/**
 * DELETE /filestorage/document/{documentLockerId}/file/{fileLockerId}
 */
export async function deleteWorkspaceFile(
  documentLockerId: string,
  fileLockerId: string,
): Promise<void> {
  const url = `${BASE_URL}/document/${encodeURIComponent(documentLockerId)}/file/${encodeURIComponent(fileLockerId)}`;
  const headers = await authHeaders();
  const response = await fetch(url, { method: 'DELETE', headers });

  if (!response.ok) {
    throw new Error(`Delete failed: ${response.status} ${response.statusText}`);
  }
}

/** File extensions supported by the convert-to-pdf endpoint */
export const CONVERT_TO_PDF_EXTS = new Set([
  'doc', 'docx', 'ppt', 'pptx', 'txt', 'html',
  'png', 'jpg', 'jpeg', 'bmp', 'tiff',
]);

/**
 * POST /filestorage/convert-to-pdf
 * Converts a supported file to PDF and returns the resulting PDF blob.
 */
export async function convertFileToPdf(file: File, signal?: AbortSignal): Promise<Blob> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (environment.aiIntegrationId) headers['aiintegrationid'] = environment.aiIntegrationId;

  const formData = new FormData();
  formData.append('file', file, file.name);

  const response = await fetch(`${BASE_URL}/convert-to-pdf`, {
    method: 'POST',
    headers,
    body: formData,
    signal,
  });

  if (!response.ok) {
    throw new Error(`convert-to-pdf failed: ${response.status} ${response.statusText}`);
  }

  return response.blob();
}
